import ErrorSummary from "./ErrorSummary";
import LayersWithDocuments from "./LayersWithDocuments";
import SendOverrideToTemplateRequestsDialog from "./SendOverrideToTemplateRequestsDialog";
import {
  outcomeErrorTitle,
  outcomeWarningsSummary,
  outcomeWarningsTitle,
  summaryTemplateErrors,
} from "./Templates.support";
import { useTemplateOverride } from "./useTemplateOverride";
import {
  GetApp,
  Delete,
  Upload,
  Download,
  Summarize,
  MoreVert,
  SendRounded as SendIcon,
} from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Component from "components/Component";
import BigLoader from "components/common/BigLoader";
import Button from "components/common/Button";
import DefaultLoader from "components/common/DefaultLoader";
import DocumentsIssued from "components/common/DocumentsIssued";
import PeerReviewNotes from "components/common/PeerReviewNotes";
import TextNote from "components/common/TextNote";
import Visible from "components/common/Visible";
import PolicyManager from "components/layers/PolicyManager";
import Endorsements from "components/policy/Endorsements";
import FixedListEndorsements from "components/policy/FixedListEndorsements";
import SpecialConditions from "components/policy/SpecialConditions";
import Subjectivities from "components/policy/Subjectivities";
import TermsAndConditions from "components/policy/TermsAndConditions";
import * as policySupport from "components/policy/support";
import * as config from "config";
import * as labels from "config.labels";
import * as fileUtils from "fileUtils";
import {
  DISPLAY_TEMPLATES_SUMMARY_BUTTON,
  NOTES_ABOVE_SUBJECTIVITIES,
  POLICY_MANAGER,
  TEMPLATE_OVERRIDE_ALLOW_PDF,
  TEMPLATE_REQUESTS_EDIT_FLOW_V2,
  TEMPLATES_DOWNLOAD_FILLED_WORD_DOCUMENT,
} from "flags";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import * as docGenService from "services/docGenService";
import * as docGenHooks from "store/hooks/docGenHooks";
import { useFeatureFlag } from "store/hooks/featureFlagHooks";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as persistenceSelectors from "store/selectors/persistence/persistenceSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";

const DOCX_MIMETYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const TemplateRow = ({
  template,
  layerIndex,
  layerId,
  submissionId,
  overrideRecord,
  refetchTemplateOverrides,
  setTemplateOverride,
  removeTemplateOverride,
}) => {
  const theme = useTheme();
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const allowPdfOverride = useFeatureFlag(TEMPLATE_OVERRIDE_ALLOW_PDF);
  const { isOn: canDownloadFilledWordDocument } = useFeatureFlag(
    TEMPLATES_DOWNLOAD_FILLED_WORD_DOCUMENT
  );
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [mappings, setMappings] = useState(null);
  const [menuLoading, setMenuLoading] = useState(null);
  const fileInputRef = useRef(null);
  const [sendOverrideOpen, setSendOverrideOpen] = useState(false);
  const [sendOverrideSuccess, setSendOverrideSuccess] = useState(false);

  const {
    downloadTemplate,
    isDownloading,
    isError,
    lastOutcome,
    clearOutcome,
  } = docGenHooks.useDownloadTemplate({ layerIndex });

  // The last fill's outcome stays beside the template until dismissed: the
  // structured error when it failed, or the values that printed as blank.
  const outcome = lastOutcome?.[template.id];
  const outcomeWarnings = outcome?.warnings ?? [];
  const warningsSummary = outcomeWarningsSummary(outcomeWarnings);
  const showOutcome = outcome?.error != null || outcomeWarnings.length > 0;
  const handleDismissOutcome = () => clearOutcome(template.id);
  const rowHasTrailer = Boolean(overrideRecord?.file_id) || showOutcome;
  const {
    downloadSummary,
    isTemplateSummaryDownloading,
    isTemplateSummaryError,
  } = docGenHooks.useGetTemplateSummary({ layerIndex });

  const {
    upload,
    remove,
    download: downloadOverrideFile,
    loadingAction,
  } = useTemplateOverride({
    template,
    submissionId,
    layerId,
    setTemplateOverride,
    removeTemplateOverride,
    refetchTemplateOverrides,
  });

  const isBusy =
    !!menuLoading ||
    loadingAction !== null ||
    isDownloading ||
    isTemplateSummaryDownloading;

  const loaderIcon = (height = 16) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: `${height}px`,
      }}
    >
      <DefaultLoader color={theme.brand.white} />
    </div>
  );

  const ensureDocx = (name) => {
    if (!name) return `${template?.title ?? "template"}.docx`;
    return name.toLowerCase().endsWith(".docx") ? name : `${name}.docx`;
  };

  const handleGenerate = () => downloadTemplate({ template });

  const handleDownloadTemplateFile = async () => {
    if (!template?.file_id) return;
    setMenuLoading("downloadTemplate");
    try {
      await fileUtils.saveFile(
        template.file_id,
        ensureDocx(template.filename ?? template.title)
      );
    } finally {
      setMenuLoading(null);
      setMenuAnchor(null);
    }
  };

  const handleDownloadFilledOriginal = async () => {
    setMenuLoading("downloadFilledOriginal");
    try {
      await downloadTemplate({
        template,
        useOriginalTemplate: true,
      });
    } finally {
      setMenuLoading(null);
      setMenuAnchor(null);
    }
  };

  const handleDownloadFilledWordDocument = async () => {
    setMenuLoading("downloadFilledWordDocument");
    try {
      await downloadTemplate({
        template,
        wordDocumentOnly: true,
      });
    } finally {
      setMenuLoading(null);
      setMenuAnchor(null);
    }
  };

  const handleOverrideClick = () => fileInputRef.current?.click();

  const handleFileSelected = async (e) => {
    const file = e?.target?.files?.[0];
    if (
      !file ||
      (file.type !== DOCX_MIMETYPE &&
        !(allowPdfOverride && file.type === "application/pdf"))
    )
      return;
    setMenuLoading("uploadOverride");
    try {
      await upload(file);
    } finally {
      setMenuLoading(null);
      if (e?.target) e.target.value = null;
      setMenuAnchor(null);
    }
  };

  const handleDownloadOverride = async () => {
    const baseName = template?.filename ?? template?.title;
    const filename =
      overrideRecord?.mime_type === "application/pdf"
        ? (baseName
            ? baseName.replace(/\.docx$/i, "")
            : template?.title ?? "template") + ".pdf"
        : ensureDocx(baseName);
    await downloadOverrideFile(overrideRecord?.file_id, filename);
  };

  const handleRemoveOverride = async () => {
    await remove();
  };

  const handleOpenSummary = () => {
    setSummaryOpen(true);
    downloadSummary({ template }).then((json) => setMappings(json));
  };

  const handleCloseSummary = () => setSummaryOpen(false);

  const summaryErrors = summaryTemplateErrors(mappings?.data);

  return (
    <>
      <TableRow key={template.id}>
        <TableCell
          sx={{
            verticalAlign: "top",
            borderBottom: rowHasTrailer && "none",
          }}
        >
          <Typography variant={"h6"}>{template.title}</Typography>
        </TableCell>
        <TableCell
          align={"right"}
          sx={{ borderBottom: rowHasTrailer && "none" }}
        >
          <Stack
            direction={"row"}
            spacing={1}
            alignItems={"center"}
            justifyContent={"flex-end"}
          >
            <Visible byTag={DISPLAY_TEMPLATES_SUMMARY_BUTTON}>
              {template.form.includes("JINJA") && (
                <Button
                  onClick={handleOpenSummary}
                  isLoading={isTemplateSummaryDownloading}
                  hasError={isTemplateSummaryError}
                  size={"small"}
                  startIcon={<Summarize />}
                  disabled={isBusy}
                >
                  {translateLabel(labels.TEMPLATES_SUMMARISE_TEMPLATE_LABEL, {
                    default: "Summarise",
                  })}
                </Button>
              )}
            </Visible>
            <Button
              onClick={handleGenerate}
              startIcon={<GetApp />}
              isLoading={isDownloading}
              hasError={isError}
              size={"small"}
              disabled={isBusy}
            >
              {translateLabel(labels.TEMPLATES_FILL_TEMPLATE_LABEL, {
                default: "Generate Document",
              })}
            </Button>
            <Tooltip title={"More actions"}>
              <span>
                <IconButton
                  size={"small"}
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  disabled={isBusy}
                >
                  <MoreVert />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>

      {overrideRecord?.file_id && (
        <TableRow>
          <TableCell
            colSpan={2}
            sx={{ pt: 0, borderBottom: showOutcome && "none" }}
          >
            <Alert
              severity={"info"}
              sx={{ mt: 1, mr: "34px", alignItems: "center" }}
              action={
                <Stack direction={"row"} spacing={1}>
                  <Button
                    size={"small"}
                    startIcon={
                      loadingAction === "download" ? loaderIcon() : <Download />
                    }
                    disabled={isBusy}
                    onClick={handleDownloadOverride}
                  >
                    {"Download"}
                  </Button>
                  <Visible byTag={TEMPLATE_REQUESTS_EDIT_FLOW_V2}>
                    <Button
                      size={"small"}
                      startIcon={<SendIcon />}
                      disabled={isBusy}
                      onClick={() => setSendOverrideOpen(true)}
                    >
                      {"Send to template requests"}
                    </Button>
                  </Visible>
                  <Button
                    size={"small"}
                    color={"error"}
                    startIcon={
                      loadingAction === "remove" ? loaderIcon() : <Delete />
                    }
                    disabled={isBusy}
                    onClick={handleRemoveOverride}
                  >
                    {"Remove"}
                  </Button>
                </Stack>
              }
            >
              {"This template has been overridden."}
            </Alert>
          </TableCell>
        </TableRow>
      )}

      {showOutcome && (
        <TableRow>
          <TableCell colSpan={2} sx={{ pt: 0 }}>
            {outcome.error ? (
              <Alert
                severity={"error"}
                sx={{ mt: 1, mr: "34px" }}
                onClose={handleDismissOutcome}
              >
                <AlertTitle>{outcomeErrorTitle(outcome.error)}</AlertTitle>
                <ErrorSummary
                  message={
                    outcome.error.summary ??
                    outcome.error.message ??
                    "The document could not be generated."
                  }
                  detail={outcome.error.detail}
                />
              </Alert>
            ) : (
              <Alert
                severity={"warning"}
                sx={{ mt: 1, mr: "34px" }}
                onClose={handleDismissOutcome}
              >
                <AlertTitle>{outcomeWarningsTitle(outcomeWarnings)}</AlertTitle>
                <ErrorSummary
                  message={warningsSummary.message}
                  detail={warningsSummary.details}
                />
              </Alert>
            )}
          </TableCell>
        </TableRow>
      )}

      <SendOverrideToTemplateRequestsDialog
        open={sendOverrideOpen}
        onClose={() => setSendOverrideOpen(false)}
        template={template}
        overrideFileId={overrideRecord?.file_id}
        submissionId={submissionId}
        layerId={layerId}
        onSent={() => setSendOverrideSuccess(true)}
      />

      <Snackbar
        open={sendOverrideSuccess}
        autoHideDuration={4000}
        onClose={() => setSendOverrideSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={"success"}
          variant={"filled"}
          onClose={() => setSendOverrideSuccess(false)}
        >
          {"Sent to template requests."}
        </Alert>
      </Snackbar>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={handleDownloadTemplateFile}
          disabled={
            !template?.file_id || isBusy || menuLoading === "downloadTemplate"
          }
        >
          <ListItemIcon>
            <Download fontSize={"small"} />
          </ListItemIcon>
          <ListItemText
            primary={translateLabel(labels.TEMPLATES_DOWNLOAD_TEMPLATE_LABEL, {
              default: "Download Template",
            })}
            secondary={translateLabel(
              labels.TEMPLATES_DOWNLOAD_TEMPLATE_DESCRIPTION,
              {
                default:
                  "Downloads the template file without filling in submission data",
              }
            )}
          />
        </MenuItem>

        {overrideRecord?.file_id && (
          <MenuItem
            onClick={handleDownloadFilledOriginal}
            disabled={isBusy || menuLoading === "downloadFilledOriginal"}
          >
            <ListItemIcon>
              <Download fontSize={"small"} />
            </ListItemIcon>
            <ListItemText
              primary={translateLabel(
                labels.TEMPLATES_DOWNLOAD_FILLED_TEMPLATE_LABEL,
                { default: "Download Filled Template" }
              )}
              secondary={translateLabel(
                labels.TEMPLATES_DOWNLOAD_FILLED_TEMPLATE_DESCRIPTION,
                {
                  default:
                    "Generates the document using the original template as configured in Marmalade",
                }
              )}
            />
          </MenuItem>
        )}

        {canDownloadFilledWordDocument &&
          template.form === "ENDORSEABLE_WORD_JINJA" && (
            <MenuItem
              onClick={handleDownloadFilledWordDocument}
              disabled={isBusy || menuLoading === "downloadFilledWordDocument"}
            >
              <ListItemIcon>
                <Download fontSize={"small"} />
              </ListItemIcon>
              <ListItemText
                primary={translateLabel(
                  labels.TEMPLATES_DOWNLOAD_FILLED_WORD_DOCUMENT_LABEL,
                  { default: "Download Filled Document (Word)" }
                )}
                secondary={translateLabel(
                  labels.TEMPLATES_DOWNLOAD_FILLED_WORD_DOCUMENT_DESCRIPTION,
                  {
                    default:
                      "Generates the filled template as a Word document, without endorsements",
                  }
                )}
              />
            </MenuItem>
          )}

        <Divider />

        <MenuItem
          onClick={handleOverrideClick}
          disabled={isBusy || menuLoading === "uploadOverride"}
        >
          <ListItemIcon>
            <Upload fontSize={"small"} />
          </ListItemIcon>
          <ListItemText
            primary={translateLabel(labels.TEMPLATES_OVERRIDE_TEMPLATE_LABEL, {
              default: "Override Template",
            })}
            secondary={translateLabel(
              labels.TEMPLATES_OVERRIDE_TEMPLATE_DESCRIPTION,
              {
                default:
                  "Uploads a document to use in place of the configured template",
              }
            )}
          />
        </MenuItem>
      </Menu>

      <input
        type={"file"}
        hidden
        ref={fileInputRef}
        accept={`.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document${
          allowPdfOverride ? ",.pdf,application/pdf" : ""
        }`}
        onChange={handleFileSelected}
      />

      <Dialog
        open={summaryOpen}
        onClose={handleCloseSummary}
        fillwidth={true}
        PaperProps={{
          style: {
            width: "800px",
            height: "90vh",
            maxWidth: "90vw",
            maxHeight: "90vh",
            borderRadius: 12,
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant={"h5"} sx={{ fontWeight: 600 }}>
              {translateLabel(labels.TEMPLATES_SUMMARY_TITLE_LABEL, {
                default: "Template Summary",
              })}
            </Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {isTemplateSummaryDownloading ? (
            <BigLoader />
          ) : (
            <>
              {mappings?.error && (
                <Alert severity={"error"} sx={{ mb: 2 }}>
                  <AlertTitle>{"Summary unavailable"}</AlertTitle>
                  <ErrorSummary
                    message={"The template summary could not be loaded."}
                    detail={String(mappings.error)}
                  />
                </Alert>
              )}
              {summaryErrors.length > 0 && (
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {summaryErrors.map((entry, index) => (
                    <Alert
                      severity={"error"}
                      key={`${
                        entry.templateId ?? entry.templateTitle
                      }-${index}`}
                    >
                      <AlertTitle>
                        {entry.templateTitle ??
                          entry.templateId ??
                          "Untitled template"}
                      </AlertTitle>
                      {entry.message}
                    </Alert>
                  ))}
                </Stack>
              )}
              <Table width={"100%"}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <h2>
                        {translateLabel(
                          labels.TEMPLATES_SUMMARY_NAME_HEAD_LABEL,
                          { default: "Name" }
                        )}
                      </h2>
                    </TableCell>
                    <TableCell>
                      <h2>
                        {translateLabel(
                          labels.TEMPLATES_SUMMARY_VALUE_HEAD_LABEL,
                          { default: "Value" }
                        )}
                      </h2>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappings?.data?.evaluations?.map((entry) => (
                    <TableRow key={entry.name}>
                      <TableCell>{entry.name}</TableCell>
                      <TableCell>{entry.value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Button onClick={handleCloseSummary}>
              {translateLabel(labels.TEMPLATES_SUMMARY_CLOSE_LABEL, {
                default: "Close",
              })}
            </Button>
            <Button
              onClick={handleGenerate}
              startIcon={<GetApp />}
              isLoading={isDownloading}
              hasError={isError}
              size={"small"}
              disabled={isBusy}
            >
              {translateLabel(labels.TEMPLATES_FILL_TEMPLATE_LABEL, {
                default: "Generate Document",
              })}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

const Templates = () => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const activeLayer = useSelector(pricingSelectors.getActiveLayerIndex);
  const layers = useSelector(pricingSelectors.getTower);
  const submissionId = useSelector(persistenceSelectors.selectSubmissionId);

  const isDocumentsIssuedVisible = useSelector(userSelectors.selectIsVisible)(
    "<Templates>.documentsIssued"
  );
  const isDocumentationNoteAboveSubjectivities = useSelector(
    userSelectors.selectIsVisible
  )(NOTES_ABOVE_SUBJECTIVITIES);

  const [layerIndex, setLayerIndex] = useState(activeLayer);

  const {
    data: fullTemplateList,
    isFetching: isFetchingTemplates,
  } = docGenService.useListTemplatesQuery();

  const layerId = layers?.[layerIndex]?.id ?? null;

  const {
    data: templateOverrides,
    refetch: refetchTemplateOverrides,
  } = docGenService.useGetTemplateOverridesQuery(
    { submissionId },
    { skip: !submissionId }
  );

  const templateOverridesByLayer = templateOverrides?.[layerId] ?? {};

  const [setTemplateOverride] = docGenService.useSetTemplateOverrideMutation();
  const [
    removeTemplateOverride,
  ] = docGenService.useRemoveTemplateOverrideMutation();

  const { config: policyConfig } = policySupport.usePolicyConfig({ layerId });

  const allForms = (fullTemplateList ?? []).filter(
    (t) => t.form !== "WORD_JINJA_ENDORSEMENT" && t.form !== "PDF_ENDORSEMENT"
  );

  const templates =
    policyConfig?.defaultVisibleForms
      ?.map((title) => allForms?.find((t) => t?.title === title))
      ?.filter((t) => t != null) ?? [];

  const documentationNote = (
    <Visible byTag={"<Templates>.documentationNote"} defaultVisible={false}>
      <TextNote noteKey={"documentation"} title={"Notes"} />
    </Visible>
  );

  return (
    <>
      <Grid item container spacing={config.GRID_SPACING}>
        <Grid item container xs={isDocumentsIssuedVisible ? 9 : 12}>
          <LayersWithDocuments
            layers={layers}
            selectedIndex={layerIndex}
            onSelectLayer={setLayerIndex}
            title={"Layers"}
            options={
              <Visible byTag={POLICY_MANAGER} defaultVisible={false}>
                <PolicyManager />
              </Visible>
            }
          />
        </Grid>
        <Visible byTag={"<Templates>.documentsIssued"} defaultVisible={false}>
          <Grid item container xs={3}>
            <DocumentsIssued layer={layers?.[layerIndex]} />
          </Grid>
        </Visible>
      </Grid>

      {!isDocumentationNoteAboveSubjectivities && documentationNote}
      <Visible byTag={"<Templates>.peerReview"} defaultVisible={false}>
        <PeerReviewNotes
          title={"Peer Review"}
          lg_width={12}
          growVertically={false}
        />
      </Visible>
      <Visible byTag={"<Templates>.endorsements"} defaultVisible={false}>
        <Endorsements layerId={layerId} />
      </Visible>
      <Visible
        byTag={"<Templates>.fixedListEndorsements"}
        defaultVisible={false}
      >
        <FixedListEndorsements layerId={layerId} />
      </Visible>
      <Visible byTag={"<Templates>.termsAndConditions"} defaultVisible={false}>
        <TermsAndConditions layerId={layers?.[layerIndex]?.id} />
      </Visible>
      <Visible byTag={"<Templates>.specialConditions"} defaultVisible={false}>
        <SpecialConditions layerId={layers?.[layerIndex]?.id} />
      </Visible>
      {isDocumentationNoteAboveSubjectivities && documentationNote}
      <Visible byTag={"<Templates>.subjectivities"} defaultVisible={false}>
        {/* Keyed so each layer remounts: Subjectivities captures per-layer
            state in refs at mount when deciding whether to apply defaults. */}
        <Subjectivities
          key={layers?.[layerIndex]?.id}
          layerId={layers?.[layerIndex]?.id}
        />
      </Visible>

      <Component
        title={translateLabel(labels.TEMPLATES_TITLE, { default: "Templates" })}
      >
        {isFetchingTemplates ? (
          <DefaultLoader
            size={60}
            style={{ display: "block", textAlign: "center" }}
          />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => {
                const overrideRecord = templateOverridesByLayer?.[template.id];
                return (
                  <TemplateRow
                    key={template.id}
                    template={template}
                    layerIndex={layerIndex}
                    layerId={layerId}
                    submissionId={submissionId}
                    overrideRecord={overrideRecord}
                    refetchTemplateOverrides={refetchTemplateOverrides}
                    setTemplateOverride={setTemplateOverride}
                    removeTemplateOverride={removeTemplateOverride}
                  />
                );
              })}
            </TableBody>
          </Table>
        )}
      </Component>
    </>
  );
};

export default Templates;
